/**
 * Kalkan Fatbike — Shopify theme interactions (vanilla JS, no jQuery).
 * Mobile drawer, popular tabs, carousel, sticky header, AJAX cart + cart drawer,
 * variant selection, quantity steppers and predictive search. Every lookup is
 * null-guarded so the script never throws on pages where an element is absent.
 */
(function () {
  'use strict';

  var routes = window.kfRoutes || {};
  var shop = window.kfShop || {};

  /* ----------------------------------------------------------------- */
  /* Helpers                                                           */
  /* ----------------------------------------------------------------- */
  function money(cents) {
    try {
      return new Intl.NumberFormat(shop.locale || 'nl-NL', {
        style: 'currency',
        currency: shop.currency || 'EUR'
      }).format((cents || 0) / 100);
    } catch (e) {
      return '€ ' + ((cents || 0) / 100).toFixed(2);
    }
  }

  function getJSON(url, opts) {
    return fetch(url, opts).then(function (r) { return r.json(); });
  }

  /* ----------------------------------------------------------------- */
  /* Mobile menu drawer                                                */
  /* ----------------------------------------------------------------- */
  function initDrawer() {
    var drawer = document.getElementById('kf-drawer');
    if (!drawer) return;
    var panel = drawer.querySelector('.kf-drawer__panel');
    var openers = document.querySelectorAll('[data-kf-drawer-open]');
    var closers = drawer.querySelectorAll('[data-kf-drawer-close]');
    var last = null;

    function focusables() {
      if (!panel) return [];
      return Array.prototype.slice.call(
        panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), select')
      ).filter(function (el) { return el.offsetParent !== null; });
    }
    function open() {
      last = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      Array.prototype.forEach.call(openers, function (b) { b.setAttribute('aria-expanded', 'true'); });
      var f = focusables(); if (f.length) f[0].focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      Array.prototype.forEach.call(openers, function (b) { b.setAttribute('aria-expanded', 'false'); });
      if (last && last.focus) last.focus();
    }
    Array.prototype.forEach.call(openers, function (b) { b.addEventListener('click', function (e) { e.preventDefault(); open(); }); });
    Array.prototype.forEach.call(closers, function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var f = focusables(); if (!f.length) return;
        var first = f[0], lastEl = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ----------------------------------------------------------------- */
  /* Popular tabs                                                      */
  /* ----------------------------------------------------------------- */
  function initTabs() {
    var bars = document.querySelectorAll('.kf-popular__tabs');
    Array.prototype.forEach.call(bars, function (bar) {
      var section = bar.closest('.kf-popular');
      if (!section) return;
      var tabs = bar.querySelectorAll('[data-kf-tab-target]');
      var panels = section.querySelectorAll('[data-kf-tab-panel]');
      var list = Array.prototype.slice.call(tabs);
      function activate(tab, focus) {
        var key = tab.getAttribute('data-kf-tab-target');
        Array.prototype.forEach.call(tabs, function (o) {
          var on = o === tab;
          o.classList.toggle('is-active', on);
          o.setAttribute('aria-selected', on ? 'true' : 'false');
          o.setAttribute('tabindex', on ? '0' : '-1');
        });
        Array.prototype.forEach.call(panels, function (p) {
          var on = p.getAttribute('data-kf-tab-panel') === key;
          p.classList.toggle('is-active', on);
          if (on) p.removeAttribute('hidden'); else p.setAttribute('hidden', 'hidden');
        });
        if (focus) tab.focus();
      }
      Array.prototype.forEach.call(tabs, function (tab, i) {
        tab.addEventListener('click', function () { activate(tab, false); });
        tab.addEventListener('keydown', function (e) {
          var t = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') t = list[(i + 1) % list.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') t = list[(i - 1 + list.length) % list.length];
          else if (e.key === 'Home') t = list[0];
          else if (e.key === 'End') t = list[list.length - 1];
          if (t) { e.preventDefault(); activate(t, true); }
        });
      });
    });
  }

  /* ----------------------------------------------------------------- */
  /* Carousels                                                         */
  /* ----------------------------------------------------------------- */
  function initCarousels() {
    var carousels = document.querySelectorAll('.kf-popular__carousel');
    Array.prototype.forEach.call(carousels, function (c) {
      var track = c.querySelector('.kf-popular__track');
      var prev = c.querySelector('[data-kf-carousel-prev]');
      var next = c.querySelector('[data-kf-carousel-next]');
      if (!track) return;
      function step() {
        var card = track.firstElementChild;
        if (card) { var s = window.getComputedStyle(track); var g = parseFloat(s.columnGap || s.gap || '0') || 0; return card.getBoundingClientRect().width + g; }
        return Math.round(track.clientWidth * 0.8);
      }
      function update() {
        var over = track.scrollWidth > track.clientWidth + 1;
        if (prev) prev.style.display = over ? '' : 'none';
        if (next) next.style.display = over ? '' : 'none';
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      update();
      window.addEventListener('resize', update, { passive: true });
    });
  }

  /* ----------------------------------------------------------------- */
  /* Sticky header                                                     */
  /* ----------------------------------------------------------------- */
  function initSticky() {
    var header = document.getElementById('kf-header');
    if (!header) return;
    function u() { header.classList.toggle('is-stuck', window.scrollY > 8); }
    u();
    window.addEventListener('scroll', u, { passive: true });
  }

  /* ----------------------------------------------------------------- */
  /* Quantity steppers (product page + cart drawer lines)              */
  /* ----------------------------------------------------------------- */
  function initQty() {
    document.addEventListener('click', function (e) {
      var up = e.target.closest('[data-kf-qty-up]');
      var down = e.target.closest('[data-kf-qty-down]');
      if (!up && !down) return;
      var wrap = (up || down).closest('.kf-qty');
      if (!wrap) return;
      var input = wrap.querySelector('.kf-qty__input');
      if (!input) return;
      var v = parseInt(input.value, 10) || 1;
      input.value = up ? v + 1 : Math.max(1, v - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ----------------------------------------------------------------- */
  /* Variant selection (product page)                                  */
  /* ----------------------------------------------------------------- */
  function initVariants() {
    var form = document.querySelector('[data-kf-product-form]');
    if (!form) return;
    var data = document.querySelector('[data-kf-variants]');
    if (!data) return;
    var variants;
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }
    var selects = form.querySelectorAll('[data-kf-option-index]');
    var idInput = form.querySelector('[data-kf-variant-id]');
    var atc = form.querySelector('[data-kf-atc]');
    var atcText = form.querySelector('[data-kf-atc-text]');

    function update() {
      var chosen = [];
      Array.prototype.forEach.call(selects, function (s) { chosen[parseInt(s.getAttribute('data-kf-option-index'), 10)] = s.value; });
      var match = variants.filter(function (v) {
        return chosen.every(function (val, i) { return v.options[i] === val; });
      })[0];
      if (!match) return;
      if (idInput) idInput.value = match.id;
      if (atc) atc.disabled = !match.available;
      if (atcText) atcText.textContent = match.available ? (window.kfStrings.addToCart || 'In winkelwagen') : (window.kfStrings.soldOut || 'Uitverkocht');
      var priceEl = document.querySelector('[data-kf-product-price]');
      if (priceEl && typeof match.price === 'number') {
        var html = '<span class="kf-price-now">' + money(match.price) + '</span>';
        if (match.compare_at_price && match.compare_at_price > match.price) {
          html += ' <span class="kf-price-old">' + money(match.compare_at_price) + '</span>';
        }
        priceEl.innerHTML = html;
      }
    }
    Array.prototype.forEach.call(selects, function (s) { s.addEventListener('change', update); });
  }

  /* ----------------------------------------------------------------- */
  /* Cart drawer + AJAX cart                                           */
  /* ----------------------------------------------------------------- */
  function initCart() {
    var drawer = document.getElementById('kf-cart-drawer');

    function openCart() {
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeCart() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    // Open via the header cart icon (suppress navigation to /cart when a drawer exists).
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-kf-cart-open]');
      if (opener && drawer) { e.preventDefault(); openCart(); refresh(); }
      if (e.target.closest('[data-kf-cart-close]')) closeCart();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) closeCart();
    });

    function setCount(n) {
      Array.prototype.forEach.call(document.querySelectorAll('[data-kf-cart-count]'), function (el) { el.textContent = n; });
    }

    function updateShipBar(cart) {
      var bar = drawer && drawer.querySelector('[data-kf-cart-shipbar]');
      if (!bar) return;
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
      if (!cart.item_count || threshold <= 0) { bar.hidden = true; return; }
      bar.hidden = false;
      var msgEl = bar.querySelector('[data-kf-shipbar-msg]');
      var fillEl = bar.querySelector('[data-kf-shipbar-fill]');
      var remaining = threshold - cart.total_price;
      if (remaining > 0) {
        if (msgEl) msgEl.innerHTML = (window.kfStrings.shipRemaining || 'Nog __AMT__ tot gratis verzending!').replace('__AMT__', '<strong>' + money(remaining) + '</strong>');
        if (fillEl) fillEl.style.width = Math.min(100, Math.round(cart.total_price * 100 / threshold)) + '%';
      } else {
        if (msgEl) msgEl.textContent = window.kfStrings.shipReached || '🎉 Gratis verzending!';
        if (fillEl) fillEl.style.width = '100%';
      }
    }

    function renderBody(cart) {
      var body = drawer && drawer.querySelector('[data-kf-cart-body]');
      var foot = drawer && drawer.querySelector('[data-kf-cart-foot]');
      setCount(cart.item_count);
      updateShipBar(cart);
      if (!body) return;
      if (!cart.item_count) {
        body.innerHTML = '<p class="kf-cart-drawer__empty">' + (window.kfStrings.cartEmpty || 'Je winkelwagen is leeg.') + '</p>';
        if (foot) foot.hidden = true;
        return;
      }
      var html = '<ul class="kf-cart-items">';
      cart.items.forEach(function (item) {
        var img = item.image ? '<img src="' + item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2') + '" alt="" width="80" height="80">' : '';
        html += '<li class="kf-cart-item" data-kf-key="' + item.key + '">' +
          '<a href="' + item.url + '" class="kf-cart-item__media">' + img + '</a>' +
          '<div class="kf-cart-item__info">' +
          '<a href="' + item.url + '" class="kf-cart-item__title">' + item.product_title + '</a>' +
          (item.variant_title ? '<div class="kf-cart-item__variant">' + item.variant_title + '</div>' : '') +
          '<div class="kf-cart-item__row">' +
          '<div class="kf-qty kf-qty--sm">' +
          '<button type="button" class="kf-qty__btn" data-kf-qty-down aria-label="-">&minus;</button>' +
          '<input type="number" class="kf-qty__input" value="' + item.quantity + '" min="0" data-kf-qty>' +
          '<button type="button" class="kf-qty__btn" data-kf-qty-up aria-label="+">+</button>' +
          '</div>' +
          '<span class="kf-cart-item__price">' + money(item.final_line_price) + '</span>' +
          '</div></div></li>';
      });
      html += '</ul>';
      body.innerHTML = html;
      if (foot) {
        foot.hidden = false;
        var sub = foot.querySelector('[data-kf-cart-subtotal]');
        if (sub) sub.textContent = money(cart.total_price);
      }
    }

    function refresh() {
      return getJSON(routes.cartJson || '/cart.js').then(renderBody).catch(function () {});
    }

    // Cart mutations are serialised: one /cart/change.js in flight at a time,
    // only the latest pending change is queued, and a non-cart response triggers
    // a clean re-fetch instead of falsely rendering an empty cart.
    var cartBusy = false, cartQueued = null;
    function flushQueue() {
      if (cartQueued) { var q = cartQueued; cartQueued = null; mutate(q.key, q.quantity); }
    }
    function mutate(key, qty) {
      if (!key) return;
      if (cartBusy) { cartQueued = { key: key, quantity: qty }; return; }
      cartBusy = true;
      getJSON(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (cart) {
        cartBusy = false;
        if (cart && typeof cart.item_count === 'number') { renderBody(cart); } else { refresh(); }
        flushQueue();
      }).catch(function () {
        cartBusy = false;
        refresh();
        flushQueue();
      });
    }

    // Line quantity controls inside the drawer (delegated, keyed by line-item key).
    document.addEventListener('click', function (e) {
      var up = e.target.closest('[data-kf-qty-up]');
      var down = e.target.closest('[data-kf-qty-down]');
      if (!up && !down) return;
      var li = (up || down).closest('[data-kf-key]');
      if (!li) return;
      var inp = li.querySelector('[data-kf-qty]');
      var cur = parseInt(inp && inp.value, 10) || 1;
      mutate(li.getAttribute('data-kf-key'), up ? cur + 1 : Math.max(0, cur - 1));
    });
    document.addEventListener('change', function (e) {
      var inp = e.target.closest('[data-kf-qty]');
      if (!inp) return;
      var li = inp.closest('[data-kf-key]');
      if (!li) return;
      mutate(li.getAttribute('data-kf-key'), Math.max(0, parseInt(inp.value, 10) || 0));
    });

    // AJAX add-to-cart for any form posting to /cart/add.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form[action$="/cart/add"]')) return;
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.classList.add('is-loading');
      fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      }).then(function (r) { return r.json(); }).then(function (res) {
        if (btn) btn.classList.remove('is-loading');
        if (res && res.status) { /* error (e.g. sold out) */ return; }
        if (drawer) { refresh().then(openCart); } else { window.location.href = routes.cart || '/cart'; }
      }).catch(function () { if (btn) btn.classList.remove('is-loading'); });
    });
  }

  /* ----------------------------------------------------------------- */
  /* Predictive search                                                 */
  /* ----------------------------------------------------------------- */
  function initPredictive() {
    var input = document.querySelector('[data-kf-predictive]');
    if (!input || !routes.predictive) return;
    var results = document.querySelector('[data-kf-predictive-results]');
    if (!results) return;
    var timer;
    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
      timer = setTimeout(function () {
        getJSON(routes.predictive + '.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
          .then(function (data) {
            var items = (data.resources && data.resources.results && data.resources.results.products) || [];
            if (!items.length) { results.hidden = true; results.innerHTML = ''; return; }
            results.innerHTML = items.map(function (p) {
              return '<a class="kf-predictive__item" href="' + p.url + '">' +
                (p.featured_image ? '<img src="' + p.featured_image.url + '" alt="" width="40" height="40">' : '') +
                '<span>' + p.title + '</span></a>';
            }).join('');
            results.hidden = false;
          }).catch(function () { results.hidden = true; });
      }, 220);
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.kf-search')) { results.hidden = true; }
    });
  }

  /* ----------------------------------------------------------------- */
  /* Product gallery (thumbnail → main image)                          */
  /* ----------------------------------------------------------------- */
  function initGallery() {
    var gallery = document.querySelector('[data-kf-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-kf-gallery-main]');
    if (!main) return;
    gallery.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-kf-thumb]');
      if (!thumb) return;
      main.src = thumb.getAttribute('data-kf-thumb');
      Array.prototype.forEach.call(gallery.querySelectorAll('[data-kf-thumb]'), function (b) { b.classList.remove('is-active'); });
      thumb.classList.add('is-active');
    });
  }

  /* ----------------------------------------------------------------- */
  /* Hero slideshow                                                    */
  /* ----------------------------------------------------------------- */
  function initSlideshow() {
    document.querySelectorAll('[data-kf-slideshow]').forEach(function (root) {
      var track = root.querySelector('[data-kf-slides]');
      if (!track) return;
      var slides = track.children;
      if (slides.length < 2) return;
      var dots = root.querySelectorAll('[data-kf-slide-dot]');
      var i = 0;
      var timer;
      track.style.display = 'flex';
      function reset() {
        if (root.getAttribute('data-autoplay') === 'true') {
          clearInterval(timer);
          timer = setInterval(function () { go(i + 1); }, parseInt(root.getAttribute('data-interval'), 10) || 6000);
        }
      }
      function go(n) {
        i = (n + slides.length) % slides.length;
        track.style.transform = 'translateX(' + (-i * 100) + '%)';
        Array.prototype.forEach.call(slides, function (s, idx) { s.classList.toggle('is-active', idx === i); });
        dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === i); });
      }
      var prev = root.querySelector('[data-kf-slide-prev]');
      var next = root.querySelector('[data-kf-slide-next]');
      if (prev) prev.addEventListener('click', function () { go(i - 1); reset(); });
      if (next) next.addEventListener('click', function () { go(i + 1); reset(); });
      dots.forEach(function (d) { d.addEventListener('click', function () { go(parseInt(d.getAttribute('data-kf-slide-dot'), 10)); reset(); }); });
      go(0);
      reset();
    });
  }

  /* ----------------------------------------------------------------- */
  /* Countdown banner                                                  */
  /* ----------------------------------------------------------------- */
  function initCountdown() {
    document.querySelectorAll('[data-kf-countdown]').forEach(function (root) {
      var daily = root.hasAttribute('data-kf-countdown-daily');
      function nextMidnight() { var n = new Date(); n.setHours(24, 0, 0, 0); return n.getTime(); }
      var deadline = root.getAttribute('data-kf-deadline');
      var end = daily ? nextMidnight() : new Date(deadline).getTime();
      if (!daily && (!deadline || isNaN(end))) return;
      var endedEl = root.querySelector('[data-kf-countdown-ended]');
      var d = root.querySelector('[data-days]'), h = root.querySelector('[data-hours]'),
          m = root.querySelector('[data-mins]'), s = root.querySelector('[data-secs]');
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      var iv;
      function tick() {
        var diff = end - Date.now();
        if (diff <= 0) {
          if (daily) { end = nextMidnight(); diff = end - Date.now(); }
          else {
            root.classList.add('is-ended');
            if (endedEl) endedEl.hidden = false;
            clearInterval(iv);
            return;
          }
        }
        if (d) d.textContent = pad(Math.floor(diff / 86400000));
        if (h) h.textContent = pad(Math.floor((diff % 86400000) / 3600000));
        if (m) m.textContent = pad(Math.floor((diff % 3600000) / 60000));
        if (s) s.textContent = pad(Math.floor((diff % 60000) / 1000));
      }
      tick();
      iv = setInterval(tick, 1000);
    });
  }

  /* ----------------------------------------------------------------- */
  /* Mega-menu: hovering a sub-category swaps the product panel         */
  /* ----------------------------------------------------------------- */
  function initMegaMenu() {
    document.querySelectorAll('[data-kf-mega]').forEach(function (mega) {
      var links = mega.querySelectorAll('[data-kf-mega-link]');
      var panels = mega.querySelectorAll('[data-kf-mega-panel]');
      if (!links.length || !panels.length) return;
      function activate(key) {
        links.forEach(function (l) { l.classList.toggle('is-active', l.getAttribute('data-kf-mega-link') === key); });
        panels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-kf-mega-panel') === key); });
      }
      links.forEach(function (l) {
        var key = l.getAttribute('data-kf-mega-link');
        l.addEventListener('mouseenter', function () { activate(key); });
        l.addEventListener('focus', function () { activate(key); });
      });
    });
  }

  /* ----------------------------------------------------------------- */
  /* Coupon: copy the discount code to the clipboard                   */
  /* ----------------------------------------------------------------- */
  function initCoupon() {
    document.querySelectorAll('[data-kf-coupon]').forEach(function (el) {
      var code = el.getAttribute('data-code') || '';
      var btn = el.querySelector('.kf-coupon__btn');
      var status = el.querySelector('.kf-coupon__status');
      if (!btn) return;
      var timer;
      function flash() {
        el.classList.add('is-copied');
        if (status) status.textContent = code + ' ' + ((window.kfStrings && window.kfStrings.copied) || 'gekopieerd');
        clearTimeout(timer);
        timer = setTimeout(function () { el.classList.remove('is-copied'); if (status) status.textContent = ''; }, 2000);
      }
      function fallback() {
        var t = document.createElement('textarea');
        t.value = code; t.setAttribute('readonly', ''); t.style.position = 'fixed'; t.style.left = '-9999px';
        document.body.appendChild(t); t.select();
        try { document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(t); flash();
      }
      btn.addEventListener('click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(flash).catch(fallback);
        } else { fallback(); }
      });
    });
  }

  function boot() {
    initDrawer();
    initTabs();
    initCarousels();
    initSticky();
    initQty();
    initVariants();
    initGallery();
    initCart();
    initPredictive();
    initSlideshow();
    initCountdown();
    initMegaMenu();
    initCoupon();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}());
